import React from 'react';

import { Form, FormControl, FormGroup, Alert } from 'react-bootstrap';
import { z } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import CollapsableItem from '../utils/CollapsableItem';
import { Activity } from '../../types/Activity';

import ActivitySectionButtons from './ActivitySectionButtons';

type Faq = Pick<Activity, 'bookingNotes'>;

type Props = Faq & {
  isMobile: boolean;
  updateActivity: (fields: Partial<Activity>) => Promise<Activity | undefined>;
};

const FormValuesSchema = z.object({
  bookingNotes: z
    .string()
    .min(1, { message: 'Booking notes must be at least 1 character long.' })
    .nullish(),
});

type FormValues = z.infer<typeof FormValuesSchema>;

export default function ActivityBookingNotesFormSection({
  bookingNotes,
  isMobile,
  updateActivity,
}: Props) {
  const {
    control,
    setValue,
    handleSubmit,
    resetField,
    trigger,
    formState,
  } = useForm<FormValues>({
    defaultValues: {
      bookingNotes,
    },
    resolver: zodResolver(FormValuesSchema),
  });

  const onSubmit = handleSubmit(async fields => {
    if (!fields.bookingNotes) return;

    const { bookingNotes } = fields;

    const experience = await updateActivity({ bookingNotes });

    if (experience?.bookingNotes) {
      setValue('bookingNotes', experience.bookingNotes);
      trigger('bookingNotes');
    }
  });

  return (
    <div className='data-box'>
      <div className='activity-section-top'>
        <h4 className='header'>Booking Notes</h4>
        <ActivitySectionButtons
          onResetClick={() =>
            resetField('bookingNotes', { defaultValue: undefined })
          }
          onSaveClick={onSubmit}
        />
      </div>
      <CollapsableItem isExpanded={!isMobile}>
        <Form>
          <FormGroup
            validationState={
              formState.errors.bookingNotes?.message ? 'error' : null
            }
          >
            <Controller
              control={control}
              name='bookingNotes'
              render={({ field }) => {
                console.log(field.value);
                return (
                  <FormControl
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    placeholder='Booking Notes'
                    type='text'
                  />
                );
              }}
            />
          </FormGroup>
          {formState.errors.bookingNotes?.message && (
            <Alert bsStyle='danger'>
              {formState.errors.bookingNotes.message}
            </Alert>
          )}
        </Form>
      </CollapsableItem>
    </div>
  );
}
